/**
 * Closed mat-select label when options use an "All" token and the control may
 * hold ["All", ...everyValue] for checkbox state.
 *
 * Cheap on change detection: O(selected) first; avoids scanning huge option
 * lists when the control is just ["All"] or obvious full-expand shape.
 */
export function multiSelectAllTriggerLabel(
  raw: string[] | null | undefined,
  options: any[] | undefined,
  allToken = 'All',
  // When the value has no "All" token but every option is selected, show "All"
  // only if the option count is at least this. Use 2 to keep a single-option
  // dropdown (e.g. a locked Segment) showing its actual value instead of "All".
  minOptionsToCollapse = 1
): string {
  const selected = Array.isArray(raw) ? raw : [];
  const selLen = selected.length;

  let hasAll = false;
  for (let i = 0; i < selLen; i++) {
    if (selected[i] === allToken) {
      hasAll = true;
      break;
    }
  }

  // Only "All" token — never scan options (critical for PSBU parts, etc.)
  if (hasAll && selLen === 1) {
    return allToken;
  }

  // Typical mat-option list has no "All" row; expanded value is ["All", ...n options]
  if (
    hasAll &&
    selLen > 1 &&
    Array.isArray(options) &&
    options.length > 0 &&
    selLen === options.length + 1
  ) {
    return allToken;
  }

  let n = 0;
  const labelBy = new Map<string, string>();
  if (Array.isArray(options)) {
    for (const o of options) {
      const v = o?.value;
      if (typeof v === 'string' && v !== allToken) {
        n++;
        labelBy.set(v, typeof o?.viewValue === 'string' ? o.viewValue : v);
      }
    }
  }

  if (hasAll && n > 0 && selLen === n + 1) {
    return allToken;
  }

  let nonAllCount = 0;
  for (let i = 0; i < selLen; i++) {
    if (selected[i] !== allToken) {
      nonAllCount++;
    }
  }
  if (!hasAll && n > 0 && n >= minOptionsToCollapse && nonAllCount === n) {
    return allToken;
  }

  if (nonAllCount === 0) {
    return selLen === 0 ? '' : allToken;
  }

  const parts: string[] = [];
  for (let i = 0; i < selLen; i++) {
    const v = selected[i];
    if (v === allToken) {
      continue;
    }
    parts.push(labelBy.get(v) ?? String(v));
  }
  return parts.join(', ');
}

/** Above this size, keep the control as ["All"] instead of selecting every option. */
export const MAX_MULTI_ALL_EXPAND = 200;

/** Mat-option nodes created for one dropdown. Larger UAT lists freeze the page. */
export const MAX_RENDERED_MULTI_OPTIONS = 200;

export function selectableValueCount(
  list: any[] | null | undefined,
  allToken = 'All'
): number {
  if (!Array.isArray(list)) {
    return 0;
  }
  let count = 0;
  for (const item of list) {
    const value = item?.value;
    if (typeof value === 'string' && value !== allToken) {
      count++;
    }
  }
  return count;
}

/** Values stored when "All" is selected. Huge lists stay as ["All"]. */
export function allModeControlValue(
  allValues: string[],
  allToken = 'All',
  max = MAX_MULTI_ALL_EXPAND
): string[] {
  if (allValues.length > max) {
    return [allToken];
  }
  return [allToken, ...allValues];
}

/**
 * Option values used to sync checkbox state. Empty when the list is too large
 * to expand, so callers never allocate or select thousands of values.
 */
export function boundedAllValues(
  list: any[] | null | undefined,
  allToken = 'All',
  max = MAX_MULTI_ALL_EXPAND
): string[] {
  if (!Array.isArray(list) || selectableValueCount(list, allToken) > max) {
    return [];
  }
  const values: string[] = [];
  for (const item of list) {
    const value = item?.value;
    if (typeof value === 'string' && value !== allToken) {
      values.push(value);
    }
  }
  return values;
}

export function limitRenderedOptions<T>(
  list: T[] | null | undefined,
  max = MAX_RENDERED_MULTI_OPTIONS
): T[] {
  if (!Array.isArray(list)) {
    return [];
  }
  return list.length > max ? list.slice(0, max) : list;
}
